import type { ReactNode } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

type UseIsTruncatingProps = {
    truncateLines: number
    children?: ReactNode
}

export const useIsTruncating = ({ truncateLines, children }: UseIsTruncatingProps) => {
    const contentRef = useRef<HTMLElement | null>(null)
    const [isTruncating, setIsTruncating] = useState(false)
    const [textContent, setTextContent] = useState('')

    const checkTruncation = useCallback(() => {
        const content = contentRef.current
        const clipContainer = content?.parentElement

        if (!content || !clipContainer || !truncateLines) return

        // The clip container carries `overflow: hidden` (and `-webkit-line-clamp`),
        // so comparing its scroll vs client box detects both vertical overflow
        // (content wrapping past `truncateLines`) and horizontal overflow (a long,
        // non-wrapping name clipped on a single line). Height-only detection missed
        // the latter, leaving long unbreakable names without a tooltip.
        const tolerance = 1
        const isOverflowing =
            clipContainer.scrollHeight - tolerance > clipContainer.clientHeight ||
            clipContainer.scrollWidth - tolerance > clipContainer.clientWidth

        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setIsTruncating(isOverflowing)
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setTextContent(content.textContent)
    }, [truncateLines])

    useLayoutEffect(() => {
        checkTruncation()
    }, [checkTruncation])

    useEffect(() => {
        const clipContainer = contentRef.current?.parentElement

        if (!clipContainer || !truncateLines) return

        const observer = new ResizeObserver(checkTruncation)

        observer.observe(clipContainer)

        return () => observer.disconnect()
    }, [truncateLines, children, checkTruncation])

    const setRef = useCallback((node: HTMLElement | null) => {
        contentRef.current = node
    }, [])

    return {
        truncateContentRef: setRef,
        isTruncating,
        textContent,
    }
}
